import { useTheme } from '../../contexts/ThemeContext';
import { usePerformance } from '../../contexts/PerformanceContext';
import { cn } from '../../utils/formatter';

interface WavesBackgroundProps {
  forceTheme?: 'light' | 'dark';
}

/**
 * Componente de fundo animado com ondas SVG de alta qualidade
 * Animações orgânicas e alternadas sem padrão de loop perceptível
 * Adaptável ao tema (light/dark) e modo de performance
 */
export const WavesBackground = ({ forceTheme }: WavesBackgroundProps = {}) => {
  // Usar valores padrão caso os contextos não estejam disponíveis
  let theme = 'light';
  let performanceMode = 'balanced';
  
  // Se forceTheme for fornecido, usar ele diretamente
  if (forceTheme) {
    theme = forceTheme;
  } else {
    try {
      const themeContext = useTheme();
      theme = themeContext?.theme || 'light';
    } catch {
      // Contexto não disponível, usar valor padrão light
      theme = 'light';
    }
  }
  
  try {
    const perfContext = usePerformance();
    performanceMode = perfContext?.performanceMode || 'balanced';
  } catch {
    // Contexto não disponível, usar valor padrão balanced
    performanceMode = 'balanced';
  }
  
  const isPerformanceMode = performanceMode === 'performance';
  const isDarkMode = theme === 'dark';

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden -z-10">
      {/* Gradiente de fundo base */}
      <div 
        className={cn(
          "absolute inset-0 transition-all duration-500",
          isDarkMode 
            ? "bg-gradient-to-br from-[#0a1929]/90 via-[#0d2137]/85 to-[#0a1929]/90"
            : "bg-gradient-to-br from-[#e8f4fb] via-[#f0f8fd] to-[#e8f4fb]"
        )}
      />
      
      {/* Máscara de tema - overlay que ajusta as ondas ao tema */}
      <div 
        className="absolute inset-0 transition-all duration-500 pointer-events-none"
        style={{
          background: isDarkMode 
            ? 'rgba(0, 0, 0, 0.65)' 
            : 'rgba(255, 255, 255, 0.75)',
          mixBlendMode: isDarkMode ? 'multiply' : 'overlay'
        }}
      />
      
      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
        style={{ filter: 'blur(0px)' }}
      >
        <defs>
          {/* Gradientes refinados para cada camada */}
          <linearGradient id="wave-gradient-1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#164194" stopOpacity={isDarkMode ? "0.15" : "0.12"} />
            <stop offset="50%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.10" : "0.08"} />
            <stop offset="100%" stopColor="#164194" stopOpacity={isDarkMode ? "0.12" : "0.10"} />
          </linearGradient>
          
          <linearGradient id="wave-gradient-2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.20" : "0.15"} />
            <stop offset="50%" stopColor="#164194" stopOpacity={isDarkMode ? "0.15" : "0.12"} />
            <stop offset="100%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.18" : "0.13"} />
          </linearGradient>
          
          <linearGradient id="wave-gradient-3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#164194" stopOpacity={isDarkMode ? "0.25" : "0.18"} />
            <stop offset="50%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.20" : "0.15"} />
            <stop offset="100%" stopColor="#164194" stopOpacity={isDarkMode ? "0.22" : "0.16"} />
          </linearGradient>
          
          <linearGradient id="wave-gradient-4" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.30" : "0.22"} />
            <stop offset="50%" stopColor="#164194" stopOpacity={isDarkMode ? "0.25" : "0.18"} />
            <stop offset="100%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.28" : "0.20"} />
          </linearGradient>
          
          <linearGradient id="wave-gradient-5" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#164194" stopOpacity={isDarkMode ? "0.35" : "0.25"} />
            <stop offset="50%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.30" : "0.22"} />
            <stop offset="100%" stopColor="#164194" stopOpacity={isDarkMode ? "0.32" : "0.23"} />
          </linearGradient>

          <linearGradient id="wave-gradient-6" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.40" : "0.28"} />
            <stop offset="50%" stopColor="#164194" stopOpacity={isDarkMode ? "0.35" : "0.25"} />
            <stop offset="100%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.38" : "0.26"} />
          </linearGradient>

          <linearGradient id="wave-gradient-7" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#164194" stopOpacity={isDarkMode ? "0.45" : "0.30"} />
            <stop offset="50%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.40" : "0.28"} />
            <stop offset="100%" stopColor="#164194" stopOpacity={isDarkMode ? "0.42" : "0.29"} />
          </linearGradient>

          <linearGradient id="wave-gradient-8" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.50" : "0.32"} />
            <stop offset="50%" stopColor="#164194" stopOpacity={isDarkMode ? "0.45" : "0.30"} />
            <stop offset="100%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.48" : "0.31"} />
          </linearGradient>

          <linearGradient id="wave-gradient-9" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#164194" stopOpacity={isDarkMode ? "0.55" : "0.35"} />
            <stop offset="50%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.50" : "0.32"} />
            <stop offset="100%" stopColor="#164194" stopOpacity={isDarkMode ? "0.52" : "0.33"} />
          </linearGradient>

          <linearGradient id="wave-gradient-10" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.60" : "0.38"} />
            <stop offset="50%" stopColor="#164194" stopOpacity={isDarkMode ? "0.55" : "0.35"} />
            <stop offset="100%" stopColor="#008BD2" stopOpacity={isDarkMode ? "0.58" : "0.36"} />
          </linearGradient>
        </defs>

        {/* Camada 1 - Movimento lento e suave */}
        <path
          fill="url(#wave-gradient-1)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,150 C320,120 640,180 960,150 C1280,120 1600,180 1920,150 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="37s"
              repeatCount="indefinite"
              keyTimes="0; 0.3; 0.6; 1"
              keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
              calcMode="spline"
              values="
                M0,150 C320,120 640,180 960,150 C1280,120 1600,180 1920,150 L1920,1080 L0,1080 Z;
                M0,180 C320,150 640,120 960,150 C1280,180 1600,120 1920,150 L1920,1080 L0,1080 Z;
                M0,165 C320,195 640,135 960,165 C1280,135 1600,195 1920,165 L1920,1080 L0,1080 Z;
                M0,150 C320,120 640,180 960,150 C1280,120 1600,180 1920,150 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 2 - Delay de 3s */}
        <path
          fill="url(#wave-gradient-2)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,220 C320,190 640,250 960,220 C1280,190 1600,250 1920,220 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="31s"
              begin="3s"
              repeatCount="indefinite"
              keyTimes="0; 0.35; 0.7; 1"
              keySplines="0.45 0.05 0.55 0.95; 0.45 0.05 0.55 0.95; 0.45 0.05 0.55 0.95"
              calcMode="spline"
              values="
                M0,220 C320,190 640,250 960,220 C1280,190 1600,250 1920,220 L1920,1080 L0,1080 Z;
                M0,250 C320,220 640,190 960,220 C1280,250 1600,190 1920,220 L1920,1080 L0,1080 Z;
                M0,235 C320,265 640,205 960,235 C1280,205 1600,265 1920,235 L1920,1080 L0,1080 Z;
                M0,220 C320,190 640,250 960,220 C1280,190 1600,250 1920,220 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 3 - Delay de 7s */}
        <path
          fill="url(#wave-gradient-3)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,290 C320,260 640,320 960,290 C1280,260 1600,320 1920,290 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="28s"
              begin="7s"
              repeatCount="indefinite"
              keyTimes="0; 0.25; 0.5; 0.75; 1"
              keySplines="0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1; 0.4 0 0.6 1"
              calcMode="spline"
              values="
                M0,290 C320,260 640,320 960,290 C1280,260 1600,320 1920,290 L1920,1080 L0,1080 Z;
                M0,320 C320,290 640,260 960,290 C1280,320 1600,260 1920,290 L1920,1080 L0,1080 Z;
                M0,275 C320,305 640,275 960,305 C1280,275 1600,305 1920,275 L1920,1080 L0,1080 Z;
                M0,305 C320,275 640,305 960,275 C1280,305 1600,275 1920,305 L1920,1080 L0,1080 Z;
                M0,290 C320,260 640,320 960,290 C1280,260 1600,320 1920,290 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 4 - Delay de 2s */}
        <path
          fill="url(#wave-gradient-4)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,360 C320,330 640,390 960,360 C1280,330 1600,390 1920,360 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="34s"
              begin="2s"
              repeatCount="indefinite"
              keyTimes="0; 0.4; 0.8; 1"
              keySplines="0.5 0 0.5 1; 0.5 0 0.5 1; 0.5 0 0.5 1"
              calcMode="spline"
              values="
                M0,360 C320,330 640,390 960,360 C1280,330 1600,390 1920,360 L1920,1080 L0,1080 Z;
                M0,390 C320,360 640,330 960,360 C1280,390 1600,330 1920,360 L1920,1080 L0,1080 Z;
                M0,345 C320,375 640,345 960,375 C1280,345 1600,375 1920,345 L1920,1080 L0,1080 Z;
                M0,360 C320,330 640,390 960,360 C1280,330 1600,390 1920,360 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 5 - Delay de 9s */}
        <path
          fill="url(#wave-gradient-5)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,430 C320,400 640,460 960,430 C1280,400 1600,460 1920,430 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="26s"
              begin="9s"
              repeatCount="indefinite"
              keyTimes="0; 0.33; 0.66; 1"
              keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
              calcMode="spline"
              values="
                M0,430 C320,400 640,460 960,430 C1280,400 1600,460 1920,430 L1920,1080 L0,1080 Z;
                M0,460 C320,430 640,400 960,430 C1280,460 1600,400 1920,430 L1920,1080 L0,1080 Z;
                M0,415 C320,445 640,415 960,445 C1280,415 1600,445 1920,415 L1920,1080 L0,1080 Z;
                M0,430 C320,400 640,460 960,430 C1280,400 1600,460 1920,430 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 6 - Delay de 5s */}
        <path
          fill="url(#wave-gradient-6)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,500 C320,470 640,530 960,500 C1280,470 1600,530 1920,500 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="29s"
              begin="5s"
              repeatCount="indefinite"
              keyTimes="0; 0.28; 0.56; 0.84; 1"
              keySplines="0.48 0.02 0.52 0.98; 0.48 0.02 0.52 0.98; 0.48 0.02 0.52 0.98; 0.48 0.02 0.52 0.98"
              calcMode="spline"
              values="
                M0,500 C320,470 640,530 960,500 C1280,470 1600,530 1920,500 L1920,1080 L0,1080 Z;
                M0,530 C320,500 640,470 960,500 C1280,530 1600,470 1920,500 L1920,1080 L0,1080 Z;
                M0,485 C320,515 640,485 960,515 C1280,485 1600,515 1920,485 L1920,1080 L0,1080 Z;
                M0,515 C320,485 640,515 960,485 C1280,515 1600,485 1920,515 L1920,1080 L0,1080 Z;
                M0,500 C320,470 640,530 960,500 C1280,470 1600,530 1920,500 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 7 - Delay de 11s */}
        <path
          fill="url(#wave-gradient-7)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,570 C320,540 640,600 960,570 C1280,540 1600,600 1920,570 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="33s"
              begin="11s"
              repeatCount="indefinite"
              keyTimes="0; 0.38; 0.76; 1"
              keySplines="0.46 0.03 0.54 0.97; 0.46 0.03 0.54 0.97; 0.46 0.03 0.54 0.97"
              calcMode="spline"
              values="
                M0,570 C320,540 640,600 960,570 C1280,540 1600,600 1920,570 L1920,1080 L0,1080 Z;
                M0,600 C320,570 640,540 960,570 C1280,600 1600,540 1920,570 L1920,1080 L0,1080 Z;
                M0,555 C320,585 640,555 960,585 C1280,555 1600,585 1920,555 L1920,1080 L0,1080 Z;
                M0,570 C320,540 640,600 960,570 C1280,540 1600,600 1920,570 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 8 - Delay de 4s */}
        <path
          fill="url(#wave-gradient-8)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,640 C320,610 640,670 960,640 C1280,610 1600,670 1920,640 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="30s"
              begin="4s"
              repeatCount="indefinite"
              keyTimes="0; 0.31; 0.62; 1"
              keySplines="0.44 0.01 0.56 0.99; 0.44 0.01 0.56 0.99; 0.44 0.01 0.56 0.99"
              calcMode="spline"
              values="
                M0,640 C320,610 640,670 960,640 C1280,610 1600,670 1920,640 L1920,1080 L0,1080 Z;
                M0,670 C320,640 640,610 960,640 C1280,670 1600,610 1920,640 L1920,1080 L0,1080 Z;
                M0,625 C320,655 640,625 960,655 C1280,625 1600,655 1920,625 L1920,1080 L0,1080 Z;
                M0,640 C320,610 640,670 960,640 C1280,610 1600,670 1920,640 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 9 - Delay de 8s */}
        <path
          fill="url(#wave-gradient-9)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,710 C320,680 640,740 960,710 C1280,680 1600,740 1920,710 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="27s"
              begin="8s"
              repeatCount="indefinite"
              keyTimes="0; 0.27; 0.54; 0.81; 1"
              keySplines="0.47 0.04 0.53 0.96; 0.47 0.04 0.53 0.96; 0.47 0.04 0.53 0.96; 0.47 0.04 0.53 0.96"
              calcMode="spline"
              values="
                M0,710 C320,680 640,740 960,710 C1280,680 1600,740 1920,710 L1920,1080 L0,1080 Z;
                M0,740 C320,710 640,680 960,710 C1280,740 1600,680 1920,710 L1920,1080 L0,1080 Z;
                M0,695 C320,725 640,695 960,725 C1280,695 1600,725 1920,695 L1920,1080 L0,1080 Z;
                M0,725 C320,695 640,725 960,695 C1280,725 1600,695 1920,725 L1920,1080 L0,1080 Z;
                M0,710 C320,680 640,740 960,710 C1280,680 1600,740 1920,710 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>

        {/* Camada 10 - Delay de 6s - Camada base mais sólida */}
        <path
          fill="url(#wave-gradient-10)"
          style={{ mixBlendMode: 'multiply' }}
          d="M0,780 C320,750 640,810 960,780 C1280,750 1600,810 1920,780 L1920,1080 L0,1080 Z"
        >
          {!isPerformanceMode && (
            <animate
              attributeName="d"
              dur="35s"
              begin="6s"
              repeatCount="indefinite"
              keyTimes="0; 0.36; 0.72; 1"
              keySplines="0.43 0.02 0.57 0.98; 0.43 0.02 0.57 0.98; 0.43 0.02 0.57 0.98"
              calcMode="spline"
              values="
                M0,780 C320,750 640,810 960,780 C1280,750 1600,810 1920,780 L1920,1080 L0,1080 Z;
                M0,810 C320,780 640,750 960,780 C1280,810 1600,750 1920,780 L1920,1080 L0,1080 Z;
                M0,765 C320,795 640,765 960,795 C1280,765 1600,795 1920,765 L1920,1080 L0,1080 Z;
                M0,780 C320,750 640,810 960,780 C1280,750 1600,810 1920,780 L1920,1080 L0,1080 Z
              "
            />
          )}
        </path>
      </svg>
    </div>
  );
};
